import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, MapPin, Globe, Mail, Phone, Calendar, Users, Star, 
  Ship, Plane, Truck, Award, Play, ExternalLink, MessageSquare,
  Linkedin, CheckCircle, Quote, ChevronRight, ArrowLeft, Clock, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';
import { getThumbnailUrl, getMediumImageUrl } from '../utils/cloudinaryUpload';

const PublicCompanyProfile = () => {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCompanyProfile();
    trackProfileView();
  }, [companyId]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/public-profile`);
      setCompany(response.data);
    } catch (error) {
      toast.error('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const trackProfileView = async () => {
    try {
      // Only track if user is logged in (token exists)
      const token = localStorage.getItem('token');
      if (token) {
        await api.post(`/companies/${companyId}/track-view`);
      }
    } catch (error) {
      // Silently fail - view tracking is not critical
      console.log('View tracking skipped');
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air': return <Plane className="h-4 w-4" />;
      case 'sea': return <Ship className="h-4 w-4" />;
      case 'land': return <Truck className="h-4 w-4" />;
      default: return <Ship className="h-4 w-4" />;
    }
  };

  const getServiceLabel = (service) => {
    const labels = {
      courier: 'Courier',
      forwarder: 'Freight Forwarder',
      nvocc: 'NVOCC',
      vessel_owner: 'Vessel Owner',
      customs_broker: 'Customs Broker',
      warehouse: 'Warehouse',
      last_mile: 'Last Mile',
      trucking: 'Trucking',
      airline_gsa: 'Airline GSA'
    };
    return labels[service] || service;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (!company) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700">Company Not Found</h2>
            <p className="text-slate-500 mt-2">This company profile is not available.</p>
            <Link to="/directory">
              <Button className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Directory
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  const themeColor = company.theme_color || '#0891b2';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Banner Section - LinkedIn/Facebook Style */}
      <div className="relative">
        {/* Cover Banner */}
        <div 
          className="h-48 md:h-64 lg:h-80 w-full bg-gradient-to-r from-slate-800 to-slate-900 relative overflow-hidden"
          style={{ 
            backgroundImage: company.banner_url ? `url(${company.banner_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {!company.banner_url && (
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-10 left-10">
                <Ship className="h-32 w-32 text-white/20" />
              </div>
              <div className="absolute bottom-10 right-10">
                <Plane className="h-24 w-24 text-white/20" />
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>

        {/* Profile Header Card */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative -mt-24 md:-mt-32 pb-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Logo */}
              <div className="flex-shrink-0">
                {toFullUrl(company.logo_url) ? (
                  <img 
                    src={toFullUrl(company.logo_url)} 
                    alt={company.legal_name}
                    className="w-28 h-28 md:w-36 md:h-36 object-contain rounded-xl border-4 border-white shadow-lg bg-white"
                  />
                ) : (
                  <div 
                    className="w-28 h-28 md:w-36 md:h-36 rounded-xl flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: themeColor }}
                  >
                    <Building2 className="h-16 w-16 text-white" />
                  </div>
                )}
              </div>

              {/* Company Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-start gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                    {company.legal_name}
                  </h1>
                  <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Verified Member
                  </Badge>
                  {company.trust_score && (
                    <Badge 
                      className="flex items-center gap-1"
                      style={{ backgroundColor: `${themeColor}20`, color: themeColor }}
                    >
                      <Star className="h-3 w-3" />
                      {company.trust_rating} ({company.trust_score})
                    </Badge>
                  )}
                </div>

                {company.tagline && (
                  <p className="text-lg text-slate-600 mb-3">{company.tagline}</p>
                )}

                {company.dba && (
                  <p className="text-sm text-slate-500 mb-2">DBA: {company.dba}</p>
                )}

                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" style={{ color: themeColor }} />
                    {company.country}
                  </span>
                  {company.website && (
                    <a 
                      href={company.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 hover:underline"
                      style={{ color: themeColor }}
                    >
                      <Globe className="h-4 w-4" />
                      Website
                    </a>
                  )}
                  {company.founded_year && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      Est. {company.founded_year}
                    </span>
                  )}
                  {company.employee_count && (
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-slate-400" />
                      {company.employee_count} employees
                    </span>
                  )}
                </div>

                {/* Services Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {company.services?.slice(0, 6).map((service, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className="text-xs"
                      style={{ borderColor: themeColor, color: themeColor }}
                    >
                      {getServiceLabel(service)}
                    </Badge>
                  ))}
                  {company.services?.length > 6 && (
                    <Badge variant="outline" className="text-xs">
                      +{company.services.length - 6} more
                    </Badge>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    className="text-white"
                    style={{ backgroundColor: themeColor }}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                  {company.social_media?.linkedin && (
                    <a href={company.social_media.linkedin} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline">
                        <Linkedin className="h-4 w-4 mr-2" />
                        LinkedIn
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex-shrink-0 hidden lg:block">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold" style={{ color: themeColor }}>
                      {company.stats?.quotes_completed || 0}
                    </div>
                    <div className="text-xs text-slate-500">Quotes Completed</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold" style={{ color: themeColor }}>
                      {company.years_in_business || 'N/A'}
                    </div>
                    <div className="text-xs text-slate-500">Years in Business</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold" style={{ color: themeColor }}>
                      {company.modes?.length || 0}
                    </div>
                    <div className="text-xs text-slate-500">Transport Modes</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold" style={{ color: themeColor }}>
                      {company.team_members?.length || 0}
                    </div>
                    <div className="text-xs text-slate-500">Team Members</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white shadow-sm rounded-lg p-1 w-full md:w-auto">
            <TabsTrigger value="overview" className="px-6">Overview</TabsTrigger>
            {company.show_team !== false && company.team_members?.length > 0 && (
              <TabsTrigger value="team" className="px-6">Team</TabsTrigger>
            )}
            {company.show_gallery !== false && company.gallery?.length > 0 && (
              <TabsTrigger value="gallery" className="px-6">Gallery</TabsTrigger>
            )}
            {company.show_testimonials !== false && company.testimonials?.length > 0 && (
              <TabsTrigger value="testimonials" className="px-6">Testimonials</TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content Column */}
              <div className="lg:col-span-2 space-y-8">
                {/* About Section */}
                {(company.about_us || company.description) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Building2 className="h-5 w-5" style={{ color: themeColor }} />
                          About Us
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 whitespace-pre-line">
                          {company.about_us || company.description}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Why Choose Us */}
                {company.why_choose_us && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Star className="h-5 w-5" style={{ color: themeColor }} />
                          Why Choose Us
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 whitespace-pre-line">{company.why_choose_us}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Mission Statement */}
                {company.mission_statement && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Card className="border-l-4" style={{ borderLeftColor: themeColor }}>
                      <CardContent className="p-6">
                        <Quote className="h-8 w-8 mb-4" style={{ color: themeColor }} />
                        <p className="text-lg italic text-slate-700">{company.mission_statement}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Video Section */}
                {company.company_video_url && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Play className="h-5 w-5" style={{ color: themeColor }} />
                          Company Video
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                          <iframe
                            src={company.company_video_url.replace('watch?v=', 'embed/')}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Featured Team Members Preview */}
                {company.show_team !== false && company.team_members?.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" style={{ color: themeColor }} />
                          Meet Our Team
                        </CardTitle>
                        {company.team_members.length > 3 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setActiveTab('team')}
                            style={{ color: themeColor }}
                          >
                            View All <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {company.team_members.slice(0, 3).map((member, idx) => (
                            <div 
                              key={member.id || idx}
                              className="text-center p-4 rounded-xl bg-slate-50 hover:shadow-md transition-shadow"
                            >
                              {member.photo_url ? (
                                <img 
                                  src={member.photo_url} 
                                  alt={member.name}
                                  className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2 border-white shadow"
                                />
                              ) : (
                                <div 
                                  className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-xl font-bold"
                                  style={{ backgroundColor: themeColor }}
                                >
                                  {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                              )}
                              <h4 className="font-semibold text-slate-900">{member.name}</h4>
                              <p className="text-sm text-slate-500">{member.title}</p>
                              {member.linkedin_url && (
                                <a 
                                  href={member.linkedin_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs mt-2 hover:underline"
                                  style={{ color: themeColor }}
                                >
                                  <Linkedin className="h-3 w-3" />
                                  LinkedIn
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Transport Modes */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Transport Modes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {company.modes?.map((mode, idx) => (
                        <Badge 
                          key={idx}
                          className="flex items-center gap-1 capitalize"
                          style={{ backgroundColor: `${themeColor}15`, color: themeColor }}
                        >
                          {getModeIcon(mode)}
                          {mode}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Certifications */}
                {company.certifications?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Award className="h-4 w-4" style={{ color: themeColor }} />
                        Certifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {company.certifications.map((cert, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span>{typeof cert === 'object' ? cert.name : cert}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Lanes Served */}
                {company.lanes_served?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Trade Lanes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {company.lanes_served.slice(0, 10).map((lane, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {lane}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Social Media */}
                {company.social_media && Object.keys(company.social_media).some(k => company.social_media[k]) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Connect With Us</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-3">
                        {company.social_media.linkedin && (
                          <a href={company.social_media.linkedin} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <Linkedin className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                        {company.social_media.facebook && (
                          <a href={company.social_media.facebook} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                            </Button>
                          </a>
                        )}
                        {company.social_media.instagram && (
                          <a href={company.social_media.instagram} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                            </Button>
                          </a>
                        )}
                        {company.social_media.tiktok && (
                          <a href={company.social_media.tiktok} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                              </svg>
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Client Logos */}
                {company.show_clients !== false && company.client_logos?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Trusted By</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3">
                        {company.client_logos.slice(0, 6).map((client, idx) => (
                          <div 
                            key={client.id || idx}
                            className="p-2 bg-slate-50 rounded-lg flex items-center justify-center"
                          >
                            {toFullUrl(client.logo_url) ? (
                              <img 
                                src={toFullUrl(client.logo_url)} 
                                alt={client.name}
                                className="max-h-8 object-contain"
                              />
                            ) : (
                              <span className="text-xs text-slate-500 text-center">{client.name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {company.team_members?.map((member, idx) => (
                <motion.div
                  key={member.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow h-full">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        {member.photo_url ? (
                          <img 
                            src={member.photo_url} 
                            alt={member.name}
                            className="w-24 h-24 rounded-full mx-auto mb-3 object-cover border-4 border-white shadow-lg"
                          />
                        ) : (
                          <div 
                            className="w-24 h-24 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl font-bold shadow-lg"
                            style={{ backgroundColor: themeColor }}
                          >
                            {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                        )}
                        <h3 className="text-lg font-bold text-slate-900">{member.name}</h3>
                        <p className="text-sm" style={{ color: themeColor }}>{member.title}</p>
                        {member.department && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {member.department}
                          </Badge>
                        )}
                      </div>

                      {member.bio && (
                        <p className="text-sm text-slate-600 mb-4 line-clamp-3">{member.bio}</p>
                      )}

                      <div className="space-y-2 text-sm">
                        {member.years_experience && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="h-4 w-4" style={{ color: themeColor }} />
                            {member.years_experience} years experience
                          </div>
                        )}
                        {member.specializations?.length > 0 && (
                          <div className="flex items-start gap-2 text-slate-600">
                            <Briefcase className="h-4 w-4 mt-0.5" style={{ color: themeColor }} />
                            <span>{member.specializations.join(', ')}</span>
                          </div>
                        )}
                        {member.languages?.length > 0 && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Globe className="h-4 w-4" style={{ color: themeColor }} />
                            {member.languages.join(', ')}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t flex items-center justify-center gap-3">
                        {member.email && (
                          <a href={`mailto:${member.email}`}>
                            <Button variant="ghost" size="sm">
                              <Mail className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                        {member.phone && (
                          <a href={`tel:${member.phone}`}>
                            <Button variant="ghost" size="sm">
                              <Phone className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                        {member.linkedin_url && (
                          <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm">
                              <Linkedin className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {company.gallery?.map((image, idx) => (
                <motion.div
                  key={image.id || idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="relative aspect-video rounded-xl overflow-hidden group cursor-pointer"
                >
                  <img 
                    src={image.url} 
                    alt={image.caption || 'Gallery image'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {(image.caption || image.category) && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                      {image.category && (
                        <Badge className="w-fit mb-2 text-xs bg-white/20 text-white">
                          {image.category}
                        </Badge>
                      )}
                      {image.caption && (
                        <p className="text-white text-sm">{image.caption}</p>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="grid md:grid-cols-2 gap-6">
              {company.testimonials?.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <Quote className="h-8 w-8 mb-4" style={{ color: `${themeColor}40` }} />
                      <p className="text-slate-700 italic mb-4">&ldquo;{testimonial.quote}&rdquo;</p>
                      
                      <div className="flex items-center gap-3">
                        {testimonial.client_logo_url ? (
                          <img 
                            src={testimonial.client_logo_url} 
                            alt={testimonial.client_name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: themeColor }}
                          >
                            {testimonial.client_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{testimonial.client_name}</p>
                          {testimonial.client_company && (
                            <p className="text-sm text-slate-500">{testimonial.client_company}</p>
                          )}
                        </div>
                        {testimonial.rating && (
                          <div className="ml-auto flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i}
                                className={`h-4 w-4 ${i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PublicCompanyProfile;
